/**
 * Art-inspired designs: Escher-style tilings and illusions, and whimsical, Seuss-style landscapes.
 * (Inspired by the styles; no specific artworks or characters are reproduced.)
 */
import type { VisualizationDef } from '../../../shared/viz';

export const circleLimit: VisualizationDef = {
  id: 'circle-limit',
  name: 'Circle Limit',
  description:
    'A hyperbolic tiling of the Poincaré disk, after Escher’s Circle Limit prints: endless tiles shrinking towards the rim, swimming through the disk. Each ring of tiles follows a frequency band.',
  tags: ['energy:mid', 'style:escher'],
  role: 'background',
  params: [
    {
      id: 'p',
      label: 'Tile sides',
      type: 'float',
      min: 0,
      max: 3,
      default: 1,
      step: 1,
      control: 'select',
      options: ['5 (pentagons)', '7 (heptagons)', '8 (octagons)', '6 (hexagons)'],
    },
    { id: 'swim', label: 'Swim', type: 'float', min: 0, max: 1, default: 0.5 },
    { id: 'spin', label: 'Spin', type: 'float', min: -1, max: 1, default: 0.15 },
    { id: 'contrast', label: 'Two-tone', type: 'float', min: 0, max: 1, default: 0.6 },
  ],
  fragment: /* glsl */ `
vec2 cmul(vec2 a, vec2 b) { return vec2(a.x * b.x - a.y * b.y, a.x * b.y + a.y * b.x); }
vec2 cdiv(vec2 a, vec2 b) { return vec2(a.x * b.x + a.y * b.y, a.y * b.x - a.x * b.y) / dot(b, b); }
void main() {
  vec2 z = (gl_FragCoord.xy - 0.5 * uResolution) / uResolution.y * 2.1;
  float rr = dot(z, z);
  if (rr >= 1.0) { fragColor = vec4(palette(0.02) * 0.03 * (1.0 - smoothstep(1.0, 1.02, rr)), 1.0); return; }
  int sel = int(uP[0] + 0.5);
  float pp = sel == 0 ? 5.0 : sel == 1 ? 7.0 : sel == 2 ? 8.0 : 6.0;
  float qq = sel == 3 ? 4.0 : 3.0;
  // Swim: a hyperbolic translation (Möbius map of the disk) along a slow circle, plus rotation.
  vec2 a = 0.45 * uP[1] * vec2(cos(uTime * 0.13), sin(uTime * 0.17));
  z = cdiv(z + a, vec2(1.0, 0.0) + cmul(vec2(a.x, -a.y), z));
  float ang = uTime * 0.1 * uP[2];
  z = mat2(cos(ang), -sin(ang), sin(ang), cos(ang)) * z;
  // Fundamental triangle: angle π/p at the centre, a right angle, π/q at the circle mirror.
  float A = PI / pp;
  float cb = cos(PI / qq), sa = sin(A);
  float cx = cb / sqrt(cb * cb - sa * sa);
  float r2 = cx * cx - 1.0;
  float flips = 0.0, inversions = 0.0;
  for (int i = 0; i < 40; i++) {
    // Fold into the wedge 0 ≤ angle ≤ π/p (rotations and reflections of the centre polygon).
    float th = atan(z.y, z.x);
    float k = floor(th / (2.0 * A));
    th -= k * 2.0 * A;
    if (th > A) { th = 2.0 * A - th; flips += 1.0; }
    z = length(z) * vec2(cos(th), sin(th));
    // Invert in the circle mirror if we're beyond it.
    vec2 d = z - vec2(cx, 0.0);
    float dd = dot(d, d);
    if (dd < r2) { z = vec2(cx, 0.0) + d * r2 / dd; inversions += 1.0; flips += 1.0; }
    else break;
  }
  float parity = mod(flips, 2.0);
  // Edges of the tile (the circle mirror) and spokes.
  float edge = abs(length(z - vec2(cx, 0.0)) - sqrt(r2));
  float shade = 1.0 - rr * 0.6;
  float band = uBands[int(min(inversions, 7.0))];
  vec3 c = mix(palette(0.15 + inversions * 0.11), palette(0.65 + inversions * 0.11), parity);
  c = mix(c, vec3(parity), uP[3] * 0.6);
  vec3 col = c * shade * (0.55 + 0.9 * band + 0.3 * pulse());
  col *= smoothstep(0.0, 0.02, edge);
  fragColor = vec4(col, 1.0);
}`,
};

export const tumblingBlocks: VisualizationDef = {
  id: 'tumbling-blocks',
  name: 'Tumbling Blocks',
  description:
    'The cube illusion: a floor of rhombi that reads as stacked cubes, then flips to read the other way on the beat.',
  tags: ['energy:mid', 'style:escher'],
  role: 'background',
  params: [
    { id: 'scale', label: 'Size', type: 'float', min: 3, max: 20, default: 8 },
    {
      id: 'flip',
      label: 'Flip on beat',
      type: 'float',
      min: 0,
      max: 1,
      default: 1,
      step: 1,
      control: 'toggle',
    },
    { id: 'drift', label: 'Drift', type: 'float', min: 0, max: 1, default: 0.3 },
  ],
  fragment: /* glsl */ `
vec4 hexCoords(vec2 uv) {
  const vec2 s = vec2(1.0, 1.7320508);
  vec4 c = floor(vec4(uv, uv - vec2(0.5, 1.0)) / s.xyxy) + 0.5;
  vec4 h = vec4(uv - c.xy * s, uv - (c.zw + 0.5) * s);
  return dot(h.xy, h.xy) < dot(h.zw, h.zw) ? vec4(h.xy, c.xy) : vec4(h.zw, c.zw + 0.5);
}
void main() {
  vec2 p = (gl_FragCoord.xy - 0.5 * uResolution) / uResolution.y;
  p += vec2(uTime * 0.02, uTime * 0.012) * uP[2];
  vec4 h = hexCoords(p * uP[0] * vec2(1.0, 1.0));
  // Rotate the hexagon so a vertex points up, then split it into three rhombi (cube faces).
  vec2 q = vec2(h.y, -h.x);
  float a = atan(q.y, q.x);
  float face = mod(floor((a + PI / 2.0) / (2.0 * PI / 3.0)), 3.0);
  // Flip: swap which faces read as lit, so the cubes appear to invert.
  float flipped = uP[1] > 0.5 ? mod(floor(uBeats), 2.0) : 0.0;
  float light = face == 0.0 ? 1.0 : face == 1.0 ? 0.55 : 0.25;
  if (flipped > 0.5) light = face == 0.0 ? 0.25 : face == 1.0 ? 1.0 : 0.55;
  float band = uBands[int(mod(h.z + h.w * 3.0, 8.0))];
  vec3 base = palette(fract((h.z + h.w) * 0.07) * 0.6 + 0.2);
  vec3 col = base * light * (0.45 + 0.8 * band + 0.25 * pulse());
  // Thin dark edges between faces.
  float edge = max(abs(q.x) * 0.866 + abs(q.y) * 0.5, abs(q.y));
  col *= smoothstep(0.5, 0.47, edge);
  float seam = min(abs(fract((a + PI / 2.0) / (2.0 * PI / 3.0)) - 0.0), abs(fract((a + PI / 2.0) / (2.0 * PI / 3.0)) - 1.0));
  col *= mix(0.7, 1.0, smoothstep(0.0, 0.03, seam * length(q)));
  fragColor = vec4(col, 1.0);
}`,
};

export const metamorphosis: VisualizationDef = {
  id: 'metamorphosis',
  name: 'Metamorphosis',
  description:
    'A two-tone tessellation whose tiles slowly transform across the screen (square, circle, diamond, star and back), each shape and its background swapping light and dark, after Escher’s Metamorphosis.',
  tags: ['energy:low', 'style:escher'],
  role: 'background',
  params: [
    { id: 'scale', label: 'Tile size', type: 'float', min: 3, max: 16, default: 7 },
    { id: 'speed', label: 'Flow', type: 'float', min: 0, max: 2, default: 0.5 },
    { id: 'outline', label: 'Outline', type: 'float', min: 0, max: 1, default: 0.5 },
    { id: 'pulse', label: 'Beat breathe', type: 'float', min: 0, max: 1, default: 0.4 },
  ],
  fragment: /* glsl */ `
// Distance to each shape (negative inside), in a cell of half-size 0.5.
float sdSquare(vec2 q, float r) { vec2 d = abs(q) - r; return max(d.x, d.y); }
float sdDiamond(vec2 q, float r) { return (abs(q.x) + abs(q.y) - r) * 0.7071; }
float sdStar(vec2 q, float r) {
  float a = atan(q.y, q.x);
  return length(q) - r * (0.62 + 0.38 * pow(abs(cos(2.0 * a)), 3.0));
}
float shapeAt(vec2 q, float m, float r) {
  // m runs 0..4 around the cycle; neighbouring shapes blend smoothly.
  float a = sdSquare(q, r * 0.88), b = length(q) - r, c = sdDiamond(q, r * 1.2), d = sdStar(q, r * 1.15);
  float f = smoothstep(0.0, 1.0, fract(m));
  int k = int(mod(floor(m), 4.0));
  if (k == 0) return mix(a, b, f);
  if (k == 1) return mix(b, c, f);
  if (k == 2) return mix(c, d, f);
  return mix(d, a, f);
}
void main() {
  vec2 p = (gl_FragCoord.xy - 0.5 * uResolution) / uResolution.y;
  vec2 g = p * uP[0] + vec2(uTime * 0.15 * uP[1], 0.0);
  vec2 cell = floor(g);
  vec2 q = fract(g) - 0.5;
  // The transformation sweeps across the screen and slowly through time.
  float m = (p.x * 0.5 + 0.5) * 3.0 + uTime * 0.05 * uP[1] + cell.y * 0.02;
  float r = 0.36 * (1.0 + 0.12 * pulse() * uP[3]);
  float d = shapeAt(q, m, r);
  float checker = mod(cell.x + cell.y, 2.0);
  float inside = step(d, 0.0);
  // Figure and ground swap on alternate tiles, as in Escher's two-tone tessellations.
  float tone = abs(inside - checker);
  vec3 dark = palette(0.12) * 0.22, light = mix(palette(0.72), vec3(0.95), 0.35);
  vec3 col = mix(dark, light, tone) * (0.8 + 0.3 * uLevel);
  col *= mix(1.0, smoothstep(0.0, 0.025, abs(d)), uP[2]);
  fragColor = vec4(col, 1.0);
}`,
};

export const truffulaGrove: VisualizationDef = {
  id: 'truffula-grove',
  name: 'Truffula Grove',
  description:
    'Whimsical candy-striped trees with fluffy tufts, swaying over rolling hills; each tuft bounces with its own frequency.',
  tags: ['energy:mid', 'style:whimsy'],
  role: 'background',
  params: [
    { id: 'trees', label: 'Trees', type: 'float', min: 3, max: 12, default: 7, step: 1 },
    { id: 'sway', label: 'Sway', type: 'float', min: 0, max: 2, default: 1 },
    { id: 'bounce', label: 'Bounce', type: 'float', min: 0, max: 2, default: 1 },
  ],
  fragment: /* glsl */ `
void main() {
  vec2 uv = gl_FragCoord.xy / uResolution;
  vec2 p = (gl_FragCoord.xy - 0.5 * uResolution) / uResolution.y;
  float aspect = uResolution.x / uResolution.y;
  // Pastel sky with a few curly stripes.
  vec3 col = mix(palette(0.55) * 0.9 + 0.15, palette(0.35) * 0.6 + 0.3, uv.y);
  col = mix(col, col * 1.08, step(0.5, fract(p.y * 6.0 + sin(p.x * 3.0 + uTime * 0.2) * 0.3)) * 0.4);
  // Rolling hills with stripes.
  float hill = -0.3 + 0.07 * sin(p.x * 3.0 + 1.0) + 0.04 * sin(p.x * 7.0);
  if (p.y < hill) {
    float stripe = step(0.5, fract((p.x + p.y * 0.6) * 5.0));
    col = mix(palette(0.2) * 0.7, palette(0.2) * 0.5, stripe);
  }
  float n = floor(uP[0]);
  for (int i = 0; i < 12; i++) {
    float fi = float(i);
    if (fi >= n) break;
    float x0 = ((fi + 0.5) / n - 0.5) * aspect * 0.95 + (hash21(vec2(fi, 1.0)) - 0.5) * 0.08;
    float base = -0.3 + 0.07 * sin(x0 * 3.0 + 1.0) + 0.04 * sin(x0 * 7.0) - 0.01;
    float height = 0.35 + 0.25 * hash21(vec2(fi, 2.0));
    float band = uBands[i % 8];
    float sway = sin(uTime * 0.8 + fi * 1.7) * 0.06 * uP[1];
    float t = clamp((p.y - base) / height, 0.0, 1.0);
    // The trunk curves more towards the top.
    float xc = x0 + sway * t * t + 0.03 * sin(t * 4.0 + fi);
    float w = 0.012 * (1.0 - 0.3 * t);
    if (p.y > base && p.y < base + height && abs(p.x - xc) < w) {
      float stripe = step(0.5, fract(t * 9.0 + (p.x - xc) * 8.0));
      col = mix(vec3(0.96, 0.94, 0.9), palette(0.05 + fi * 0.1), stripe) * (0.8 + 0.4 * (1.0 - abs(p.x - xc) / w));
    }
    // A fluffy tuft that bounces with the tree's band.
    vec2 top = vec2(x0 + sway, base + height);
    vec2 d = p - top - vec2(0.0, 0.02 + 0.03 * band * uP[2]);
    float a = atan(d.y, d.x);
    float r = (0.075 + 0.025 * band * uP[2]) * (0.85 + 0.15 * sin(a * 9.0 + uTime + fi) + 0.06 * fbm(vec2(a * 3.0, fi + uTime * 0.3)));
    float tuft = smoothstep(r, r - 0.012, length(d * vec2(1.0, 1.25)));
    vec3 tuftCol = palette(fi * 0.137 + 0.3) * (0.75 + 0.4 * band + 0.2 * pulse());
    col = mix(col, tuftCol * (0.85 + 0.25 * smoothstep(0.0, 0.08, d.y + 0.03)), tuft);
  }
  fragColor = vec4(col, 1.0);
}`,
};

export const whimsyTowers: VisualizationDef = {
  id: 'whimsy-towers',
  name: 'Whimsy Towers',
  description:
    'Wobbly, striped storybook towers topped with curly spirals, bouncing and leaning with the beat.',
  tags: ['energy:mid', 'style:whimsy'],
  role: 'background',
  params: [
    { id: 'towers', label: 'Towers', type: 'float', min: 2, max: 9, default: 5, step: 1 },
    { id: 'wobble', label: 'Wobble', type: 'float', min: 0, max: 2, default: 1 },
    { id: 'bounce', label: 'Bounce', type: 'float', min: 0, max: 2, default: 1 },
  ],
  fragment: /* glsl */ `
void main() {
  vec2 uv = gl_FragCoord.xy / uResolution;
  vec2 p = (gl_FragCoord.xy - 0.5 * uResolution) / uResolution.y;
  float aspect = uResolution.x / uResolution.y;
  vec3 col = mix(palette(0.6) * 0.5 + 0.35, palette(0.85) * 0.4 + 0.45, uv.y);
  // Dotted ground.
  if (p.y < -0.38) {
    vec2 g = fract(p * 20.0) - 0.5;
    col = mix(palette(0.25) * 0.6, palette(0.25) * 0.8, smoothstep(0.25, 0.2, length(g)));
  }
  float n = floor(uP[0]);
  for (int i = 0; i < 9; i++) {
    float fi = float(i);
    if (fi >= n) break;
    float x0 = ((fi + 0.5) / n - 0.5) * aspect * 0.9;
    float band = uBands[i % 8];
    float h = 0.45 + 0.3 * hash21(vec2(fi, 4.0)) + 0.05 * band * uP[2];
    float base = -0.38;
    float t = (p.y - base) / h;
    if (t < 0.0 || t > 1.08) continue;
    // Lean and wobble: more at the top, a little bounce on the beat.
    float lean = sin(uTime * 0.7 + fi * 2.1) * 0.08 * uP[1] * t * t + pulse() * 0.02 * uP[2] * t;
    float xc = x0 + lean + 0.015 * sin(t * 12.0 + uTime + fi) * uP[1];
    // Stacked bulging segments.
    float seg = fract(t * 5.0);
    float w = (0.05 + 0.02 * sin(seg * PI)) * (1.0 - 0.35 * t);
    if (t <= 1.0 && abs(p.x - xc) < w) {
      float stripe = step(0.5, fract((p.x - xc) * 18.0 + t * 3.0 + fi));
      vec3 c = mix(palette(fi * 0.17), vec3(0.97), stripe * 0.85);
      col = c * (0.75 + 0.35 * (1.0 - abs(p.x - xc) / w)) * (0.85 + 0.3 * band);
    }
    // A curly spiral on top.
    vec2 top = vec2(x0 + lean + 0.015 * sin(12.0 + uTime + fi), base + h + 0.03);
    vec2 d = p - top;
    float r = length(d), a = atan(d.y, d.x) + uTime * 0.8;
    float spiral = abs(fract((r * 60.0 - a / TAU) ) - 0.5);
    col = mix(col, palette(fi * 0.17 + 0.5), smoothstep(0.18, 0.08, spiral) * smoothstep(0.045, 0.035, r));
  }
  fragColor = vec4(col, 1.0);
}`,
};

export const ART_VISUALIZATIONS: VisualizationDef[] = [
  circleLimit,
  tumblingBlocks,
  metamorphosis,
  truffulaGrove,
  whimsyTowers,
];
